import { Vue, Component } from 'vue-property-decorator'
import { Auth } from 'aws-amplify'
import { AmplifyEventBus } from 'aws-amplify-vue'
import { CognitoIdentityServiceProvider } from 'aws-sdk'
import awsconfig from '../aws-exports'
import { User } from '@/types'

interface IAuthService {
	authenticated: boolean
	user: any
	signOut: () => void
	changePassword: (oldPass: string, newPass: string) => Promise<any>
	listUsers: () => Promise<User[]>
	createUser: (email: string, pass: string) => Promise<User>
	deleteUser: (id: string) => Promise<void>
}

@Component
class AuthService extends Vue implements IAuthService {

	authenticated = false
	user: any = null

	private idpServiceProvider: CognitoIdentityServiceProvider | null = null

	created() {
		this.getUser()
		AmplifyEventBus.$on('authState', () => this.getUser())
	}

	signOut() {
		Auth.signOut().then(() => AmplifyEventBus.$emit('authState', 'signedOut'))
	}

	changePassword(oldPass: string, newPass: string) {
		return Auth.changePassword(this.user, oldPass, newPass)
	}

	listUsers() {
		return new Promise<User[]>((resolve, reject) => {
			this.idpServiceProvider!.listUsers({
				UserPoolId: awsconfig.aws_user_pools_id
			}, (err, data) => {
				if(err) {
					return reject(err)
				}
				const users: User[] = []
				if(data.Users) {
					data.Users.forEach(u => {
						let email
						if(u.Attributes) {
							email = (u.Attributes.find(att => att.Name === 'email') || { Value: '' }).Value
						}
						if(email && u.Username)
						users.push({
							name: u.Username,
							email
						})
					})
				}
				resolve(users)
			})
		})
	}

	createUser(email: string, pass: string) {
		return new Promise<User>((resolve, reject) => {
			this.idpServiceProvider!.adminCreateUser({
				UserPoolId: awsconfig.aws_user_pools_id,
				MessageAction: 'SUPPRESS',
				Username: email,
				TemporaryPassword: pass,
				UserAttributes: [
					{ Name: 'email', Value: email },
					{ Name: 'email_verified', Value: 'True' }
				]
			}, (err, data) => {
				if(err) {
					return reject(err)
				}
				resolve({
					name: data.User!.Username!,
					email
				})
			})
		})
	}

	deleteUser(id: string) {
		return new Promise<void>((resolve, reject) => {
			this.idpServiceProvider!.adminDeleteUser({
				UserPoolId: awsconfig.aws_user_pools_id,
				Username: id
			}, (err) => {
				if(err) {
					return reject(err)
				}
				resolve()
			})
		})
	}

	private async getUser() {
		try {
			const user = await Auth.currentAuthenticatedUser()
			const credentials = await Auth.currentCredentials()

			this.authenticated = true
			this.idpServiceProvider = new CognitoIdentityServiceProvider({
				apiVersion: "2016-04-18",
				region: awsconfig.aws_appsync_region,
				credentials: Auth.essentialCredentials(credentials)
			})
			this.user = user
		} catch(err) {
			if(err !== 'not authenticated') {
				throw err
			}
			this.authenticated = false
			this.user = null
			this.idpServiceProvider = null
		}
	}

}

export const $auth: IAuthService = new AuthService()
